/** @jsxImportSource @emotion/react */
import { useCallback } from 'react';
import {
  Alert,
  Badge,
  Col,
  Container,
  Placeholder,
  Row,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useParams, Outlet, useNavigate, Link } from 'react-router-dom';
import { css } from '@emotion/react';
import { BlobProvider } from '@react-pdf/renderer';

import InvoicePDF from './InvoicePDF';
import { api } from 'api';
import { formatDate } from 'app/utils/formatDate';
import { formatCurrency } from 'app/utils/formatCurrency';

import { Invoice, Customer } from 'types';
import InvoiceActions from './InvoiceActions';
import EditButton from './EditButton';

const InvoiceShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data,
    refetch,
    isLoading: isLoadingData,
    isError,
  } = api.useQuery('get', '/invoices/{id}', {
    params: { path: { id: Number(id) } },
  });

  const invoice = data as Invoice & { customer: Customer };
  const customer = invoice?.customer;

  const onSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const onDelete = useCallback(async () => {
    navigate('/');
  }, [navigate]);

  if (isError) {
    return (
      <Container>
        <Alert variant="warning">There was an error loading the data</Alert>
      </Container>
    );
  }

  if (isLoadingData) {
    return (
      <Container className="mt-5">
        <Row>
          <Col>
            <Placeholder as="p" animation="glow">
              <Placeholder xs={12} />
              <Placeholder xs={12} />
            </Placeholder>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Invoice #${invoice?.id} - ${customer?.first_name} ${customer?.last_name}`}</title>
      </Helmet>

      <Container className="mt-5">
        <Row>
          <Col>
            <Link
              to="/"
              css={css`
                font-weight: bold;
                font-size: 1.4rem;
                text-decoration: none;
                color: #0d6efd;

                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              &larr; Invoices List
            </Link>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col className="d-flex justify-content-end">
            <BlobProvider document={<InvoicePDF invoice={invoice} />}>
              {({ url, loading }) =>
                loading ? (
                  <span>Loading PDF...</span>
                ) : (
                  <a
                    href={url ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: 'none',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#0d6efd',
                      color: 'white',
                      borderRadius: '0.375rem',
                    }}
                  >
                    View Invoice PDF
                  </a>
                )
              }
            </BlobProvider>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs={12} className="d-flex justify-content-end">
            <EditButton finalized={invoice?.finalized} id={id} />
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6}>
            <div>
              <strong>
                {customer?.first_name} {customer?.last_name}
              </strong>
            </div>
            <div>{customer?.address}</div>
            <div>
              {customer?.zip_code} {customer?.city}
            </div>
            <div>{customer?.country}</div>
          </Col>
          <Col xs={12} md={6}>
            <div>
              <strong>Invoice</strong>{' '}
              {invoice?.finalized && <Badge bg="primary">Finalized</Badge>}
            </div>
            <div>#{invoice?.id}</div>
            <div>Date: {formatDate(invoice?.date)}</div>
            <div>
              Deadline:{' '}
              {invoice?.deadline ? formatDate(invoice?.deadline) : 'Not set'}
            </div>
            <div>
              Status:{' '}
              <Badge
                css={css`
                  background-color: ${invoice?.paid ? '#198754' : '#d9534f'};
                  color: white;
                `}
              >
                {invoice?.paid ? 'Paid' : 'Unpaid'}
              </Badge>
            </div>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col>
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ width: '100px' }}>Quantity</th>
                  <th style={{ width: '130px' }}>Price</th>
                  <th style={{ width: '130px' }}>Tax</th>
                </tr>
              </thead>
              <tbody>
                {invoice?.invoice_lines?.map((line) => (
                  <tr key={line.id}>
                    <td>{line.label}</td>
                    <td>{line.quantity}</td>
                    <td>{formatCurrency(line.price)}</td>
                    <td>{formatCurrency(line.tax)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={1} />
                  <th>Total</th>
                  <td>{formatCurrency(invoice?.total)}</td>
                  <td>{formatCurrency(invoice?.tax)}</td>
                </tr>
              </tfoot>
            </table>
          </Col>
        </Row>

        {!!invoice && (
          <InvoiceActions
            invoice={invoice}
            onSuccess={onSuccess}
            onDeleteSuccess={onDelete}
          />
        )}
      </Container>

      <Outlet />
    </>
  );
};

export default InvoiceShow;
